/**
 * Deployment Type Presets
 * When a tenant selects a deployment type, these defaults are auto-loaded
 * into the form: recommended login methods, portal template, branding, policies.
 */

export const DEPLOYMENT_TYPES = [
    {
        category: 'hospitality',
        label: 'Hospitality',
        icon: 'fa-hotel',
        color: '#f59e0b',
        subcategories: [
            { value: 'hotel',             label: 'Hotel' },
            { value: 'resort',            label: 'Resort' },
            { value: 'motel',             label: 'Motel' },
            { value: 'hostel',            label: 'Hostel' },
            { value: 'guest_house',       label: 'Guest House' },
            { value: 'service_apartment', label: 'Service Apartment' },
        ],
    },
    {
        category: 'enterprise',
        label: 'Enterprise',
        icon: 'fa-building',
        color: '#6366f1',
        subcategories: [
            { value: 'corporate_office',  label: 'Corporate Office' },
            { value: 'government_office', label: 'Government Office' },
            { value: 'business_park',     label: 'Business Park' },
        ],
    },
    {
        category: 'education',
        label: 'Education',
        icon: 'fa-graduation-cap',
        color: '#10b981',
        subcategories: [
            { value: 'university', label: 'University' },
            { value: 'college',    label: 'College' },
            { value: 'school',     label: 'School' },
        ],
    },
    {
        category: 'healthcare',
        label: 'Healthcare',
        icon: 'fa-hospital',
        color: '#ef4444',
        subcategories: [
            { value: 'hospital', label: 'Hospital' },
            { value: 'clinic',   label: 'Clinic' },
        ],
    },
    {
        category: 'commercial',
        label: 'Commercial',
        icon: 'fa-store',
        color: '#8b5cf6',
        subcategories: [
            { value: 'shopping_mall',   label: 'Shopping Mall' },
            { value: 'retail_store',    label: 'Retail Store' },
            { value: 'supermarket',     label: 'Supermarket' },
            { value: 'coworking_space', label: 'Co-working Space' },
        ],
    },
    {
        category: 'residential',
        label: 'Residential',
        icon: 'fa-home',
        color: '#06b6d4',
        subcategories: [
            { value: 'apartment',       label: 'Apartment' },
            { value: 'condominium',     label: 'Condominium' },
            { value: 'housing_society', label: 'Housing Society' },
        ],
    },
    {
        category: 'transportation',
        label: 'Transportation',
        icon: 'fa-plane',
        color: '#f97316',
        subcategories: [
            { value: 'airport',          label: 'Airport' },
            { value: 'railway_station',  label: 'Railway Station' },
            { value: 'bus_terminal',     label: 'Bus Terminal' },
        ],
    },
    {
        category: 'public_wifi',
        label: 'Public Wi-Fi',
        icon: 'fa-wifi',
        color: '#ec4899',
        subcategories: [
            { value: 'cafe',        label: 'Cafe' },
            { value: 'restaurant',  label: 'Restaurant' },
            { value: 'library',     label: 'Library' },
            { value: 'public_park', label: 'Public Park' },
        ],
    },
    {
        category: 'custom',
        label: 'Custom',
        icon: 'fa-cogs',
        color: '#6b7280',
        subcategories: [
            { value: 'custom', label: 'Custom Deployment' },
        ],
    },
];

/**
 * Default settings that auto-load when a deployment type is selected.
 * Keys are category names.
 */
export const DEPLOYMENT_PRESETS = {
    hospitality: {
        loginMethods:   ['pms', 'voucher', 'sms_otp'],
        portalTemplate: 'hotel',
        sessionTimeout: 86400,   // 24h
        idleTimeout:    3600,    // 1h
        deviceLimit:    5,
        description:    'Hotel/resort guest authentication via PMS room number, vouchers, or OTP.',
        brandingTheme:  'warm',
        defaultPlan:    'Unlimited 24h',
    },
    enterprise: {
        loginMethods:   ['username_password', 'sms_otp', 'email_otp'],
        portalTemplate: 'enterprise',
        sessionTimeout: 28800,   // 8h (work day)
        idleTimeout:    1800,
        deviceLimit:    3,
        description:    'Corporate staff and guest access with secure credentials.',
        brandingTheme:  'corporate',
    },
    education: {
        loginMethods:   ['username_password', 'sms_otp'],
        portalTemplate: 'enterprise',
        sessionTimeout: 43200,   // 12h
        idleTimeout:    1800,
        deviceLimit:    2,
        description:    'University/school student and staff access control.',
        brandingTheme:  'light',
    },
    healthcare: {
        loginMethods:   ['voucher', 'username_password'],
        portalTemplate: 'enterprise',
        sessionTimeout: 14400,   // 4h
        idleTimeout:    900,
        deviceLimit:    2,
        description:    'Patient and staff Wi-Fi with compliance controls.',
        brandingTheme:  'light',
    },
    commercial: {
        loginMethods:   ['sms_otp', 'social', 'voucher'],
        portalTemplate: 'shopping_mall',
        sessionTimeout: 7200,    // 2h
        idleTimeout:    600,
        deviceLimit:    3,
        description:    'Retail/mall visitor access with marketing capture.',
        brandingTheme:  'vibrant',
    },
    residential: {
        loginMethods:   ['username_password', 'voucher'],
        portalTemplate: 'enterprise',
        sessionTimeout: 0,       // unlimited
        idleTimeout:    0,
        deviceLimit:    10,
        description:    'Apartment/condo resident unlimited access.',
        brandingTheme:  'light',
    },
    transportation: {
        loginMethods:   ['sms_otp', 'social', 'voucher'],
        portalTemplate: 'airport',
        sessionTimeout: 7200,
        idleTimeout:    600,
        deviceLimit:    2,
        description:    'Airport/station traveller access with time limits.',
        brandingTheme:  'modern',
    },
    public_wifi: {
        loginMethods:   ['sms_otp', 'social'],
        portalTemplate: 'cafe',
        sessionTimeout: 3600,    // 1h
        idleTimeout:    300,
        deviceLimit:    2,
        description:    'Cafe/restaurant visitor access with SMS or social login.',
        brandingTheme:  'vibrant',
    },
    custom: {
        loginMethods:   [],
        portalTemplate: 'custom',
        sessionTimeout: 3600,
        idleTimeout:    600,
        deviceLimit:    1,
        description:    '',
        brandingTheme:  'light',
    },
};

export function getPreset(category) {
    return DEPLOYMENT_PRESETS[category] || DEPLOYMENT_PRESETS.custom;
}

export function getCategoryBySubcategory(subcategory) {
    for (const dt of DEPLOYMENT_TYPES) {
        if (dt.subcategories.some(s => s.value === subcategory)) return dt.category;
    }
    return 'custom';
}
